import apiClient from "../../../utils/apiClient";
import { getBatchEnrollmentRequests } from "../../../utils/enrollmentApi";
import { useResource } from "../../Students/StudentProfile/useStudentRecord";

/**
 * Everything the batch page reads up front, fetched in parallel.
 *
 * Only `batch` is required to render. Each of the others fails on its own and
 * costs the page one figure, never the page: a sub-admin without
 * `settings_zoom` gets a 403 from the Zoom settings, and that must not hide the
 * roster.
 *
 * The pending-requests count is read HERE rather than by the Requests tab
 * because a tab's body does not mount until it is opened — and a count that
 * appears only after you look is not a badge.
 */
export default function useBatchDetails(batchId, { readZoomSettings = false } = {}) {
  const batch = useResource(batchId, () =>
    apiClient.get(`/batches/${batchId}`).then((r) => r.data.batch || null));
  const stats = useResource(batchId, () =>
    apiClient.get(`/batches/${batchId}/stats`).then((r) => r.data));
  const assignmentCount = useResource(batchId, () =>
    apiClient.get("/general-assignments/list", { params: { batchId } })
      .then((r) => r.data.assignments?.length || 0));
  // Whether the batch has a class plan of ANY kind — days, classes formed from
  // modules, or Smart Learning sessions. A failure reads as "no plan".
  const hasSyllabus = useResource(batchId, () =>
    apiClient.get(`/syllabus/batch/${batchId}/plan`)
      .then((r) => (r.data?.sessions?.length || 0) > 0, () => false));
  const pendingRequests = useResource(batchId, () =>
    getBatchEnrollmentRequests(batchId).then((r) => r.data?.data?.counts?.actionable || 0));
  const zoom = useResource(batchId, () =>
    apiClient.get("/institute/zoom-settings").then((r) => r.data), readZoomSettings);

  return { batch, stats, assignmentCount, hasSyllabus, pendingRequests, zoom };
}
